import { useCallback, useEffect, useRef, useState } from 'react';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import { noteOperations, type NoteOperation } from './studyNotebookOperationsApi';
import { errorCode } from './studyAnalysisErrors';

export function useAudioScriptDraft(noteId: string, operation: NoteOperation) {
  const owner = useAuthStore(() => getKidSub());
  const [script, setScript] = useState(operation.result?.script ?? '');
  const [status, setStatus] = useState('Saved');
  const state = useRef({
    text: script,
    saved: script,
    revision: operation.revision,
    request: null as { text: string; id: string } | null,
  });
  const pending = useRef<Promise<void> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const live = useCallback(() => Boolean(owner && getKidSub() === owner), [owner]);
  const flush = useCallback((): Promise<void> => {
    if (pending.current)
      return pending.current.then(() =>
        state.current.saved !== state.current.text ? flush() : undefined,
      );
    if (!live() || state.current.saved === state.current.text) return Promise.resolve();
    const text = state.current.text;
    if (state.current.request?.text !== text)
      state.current.request = { text, id: crypto.randomUUID() };
    setStatus('Saving draft…');
    const task = noteOperations
      .change<{ revision: number; script: string }>(noteId, operation.id, 'audio-draft', {
        expected_revision: state.current.revision,
        script: text,
        request_id: state.current.request.id,
      })
      .then((row) => {
        if (!live()) return;
        state.current.saved = text;
        state.current.revision = row.revision;
        state.current.request = null;
        setStatus(state.current.text === text ? 'Draft saved' : 'Unsaved draft');
      })
      .catch((error) => {
        if (live())
          setStatus(
            errorCode(error) === 'STUDY_AUDIO_SCRIPT_CHANGED'
              ? 'This script changed elsewhere. Your words are still here. Reopen the saved draft before saving again.'
              : 'Draft not saved. Your words are still here. Retry before leaving.',
          );
        throw error;
      })
      .finally(() => {
        pending.current = null;
      });
    pending.current = task;
    return task.then(() => (state.current.saved !== state.current.text ? flush() : undefined));
  }, [live, noteId, operation.id]);
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      if (live()) void flush().catch(() => undefined);
    },
    [owner, operation.id, live, flush],
  );
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (live() && state.current.text !== state.current.saved) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [owner, operation.id, live]);
  return {
    script,
    status,
    dirty: script !== state.current.saved,
    revision: () => state.current.revision,
    change: (text: string) => {
      state.current.text = text;
      setScript(text);
      setStatus('Unsaved draft');
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (live()) void flush().catch(() => undefined);
      }, 700);
    },
    flush,
    reload: async () => {
      if (!live()) return;
      clearTimeout(timer.current);
      if (pending.current) await pending.current.catch(() => undefined);
      if (
        state.current.saved !== state.current.text &&
        !window.confirm(
          'Replace these unsaved words with the saved script? Copy any words you want to keep before continuing.',
        )
      )
        return;
      const row = await noteOperations.get(noteId, operation.id).catch((error) => {
        if (live())
          setStatus(
            'The saved script could not reopen. Your current words are still here. Try again.',
          );
        throw error;
      });
      if (!live()) return;
      if (row.status !== 'awaiting_approval') {
        setStatus(
          'This script is no longer waiting for review. Reopen the learning item; your words are still here.',
        );
        throw new Error('This script is no longer waiting for review. Reopen the learning item.');
      }
      const text = row.result?.script ?? '';
      state.current = { text, saved: text, revision: row.revision, request: null };
      setScript(text);
      setStatus('Draft saved');
    },
  };
}
